import React, { FC, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { stepInfoFulfilled } from '../../../../../../actions/registerShipmentActions';
import { getCountry, getPostcodeLabel } from '../../../../../../helpers/apiHelpers/registerShipmentHelpers';
import { filterCountries } from '../../../../../../helpers/select/filterCountries';
import { getCurrentLangPretty } from '../../../../../../helpers/url/urlHelpers';
import useCountries from '../../../../../../hooks/useCountries';
import usePostCodes from '../../../../../../hooks/usePostCodes';
import useRecipients from '../../../../../../hooks/useRecipients';
import { IFormSelectOption } from '../../../../../../interfaces/forms/forms';
import IUserProfileReducer from '../../../../../../interfaces/reducers/userProfileReducer';
import { IStore } from '../../../../../../reducers';
import store from '../../../../../../store';
import FormikCheckbox from '../../../../../base/forms/formikCheckbox';
import FormikFieldWrapper from '../../../../../base/forms/formikFieldWrapper';
import FormikSelect from '../../../../../base/forms/formikSelect';
import { StyledFormikFastField } from '../../../../../base/forms/input';
import { Label } from '../../../../../base/forms/label';
import PhoneCountryInput from '../../../../../base/forms/phoneCountryInput';
import { Column } from '../../../../../base/grid';
import { A } from '../../../../../base/typography/anchors';
import * as StyledStepType from '../../../stepType/stepType.styles';
import { warnings } from '../../../stepType/stepTypeForm/helpers';
import { getBusinessAccountRegisterShipmentUrl, requiresBusinessAccountForCompanySender } from '../../helpers';
import { IStepInfoFormInfo } from '../../stepInfo.schema';
import * as StyledStepInfoForm from '../stepInfoForm.styles';

interface Props {
	values: IStepInfoFormInfo;
	setFieldTouched: any;
	setFieldValue: any;
	goingToPostbox: boolean;
	validateForm: any;
}

const ReceiverFields: FC<Props> = ({ values, setFieldTouched, setFieldValue, goingToPostbox, validateForm }) => {
	const { readOnlyValuesMapper } = useSelector(({ registerShipmentReducer }) => registerShipmentReducer);
	const { t } = useTranslation('registerShipment');
	const { postCodes } = usePostCodes();
	const lang = getCurrentLangPretty();
	const { countries } = useCountries(lang, true);
	const showBusinessAccountWarning = requiresBusinessAccountForCompanySender(values);
	const businessAccountUrl = getBusinessAccountRegisterShipmentUrl(lang);
	// const [emailWarning, setEmailWarning] = useState(null);

	const userProfileReducer: IUserProfileReducer = useSelector<IStore, IUserProfileReducer>(
		store => store.userProfileReducer
	);
	const { userProfile, isLoggedIn } = userProfileReducer;
	const { recipients } = useRecipients(userProfile);

	const dispatch = useDispatch<any>();
	useEffect(() => {
		const { registerShipmentReducer } = store.getState();
		const { stepInfoFormInfo: infoFormInfo } = registerShipmentReducer;
		const { country } = values;

		if (country.value !== infoFormInfo.country.value) {
			dispatch(stepInfoFulfilled(values));
		}
	}, [dispatch, values, values.country]);

	const recipientsAsSelectOptions = recipients => {
		const selectOptions = [];
		recipients.map(recipient => {
			return selectOptions.push({ value: recipient.id, label: recipient.name });
		});
		return selectOptions;
	};

	const loadInRecipient = (key, option) => {
		const recipient = recipients.find(rec => rec.id === option.value);

		setFieldValue('receiverName', recipient.name);
		setFieldValue('receiverSsn', recipient.identificationNumber);
		setFieldValue('receiverAddress', recipient.address?.street1);
		setFieldValue('receiverEmail', recipient.email);
		setFieldValue('receiverPhone', recipient.phone);
		const recipientCountry = getCountry(recipient.address?.country, countries);
		setFieldValue(
			'country',
			recipientCountry || {
				value: recipient.address?.country,
				label: recipient.address?.country,
			}
		);

		if (recipient.address?.country.toLowerCase() === 'is') {
			setFieldValue('receiverPostCode', {
				value: recipient.address?.postalCode,
				label: getPostcodeLabel(recipient.address?.postalCode, postCodes),
			});
		} else {
			setFieldValue('receiverForeignAddressExtended', recipient.address?.street2);
			setFieldValue('receiverForeignPlace', recipient.address?.city);
			setFieldValue('receiverForeignPostCode', recipient.address?.postalCode);
		}

		setFieldValue(key, option);
	};

	useEffect(() => {
		validateForm();
	}, [values.receiverForeignPostCode, validateForm]);
	// const handleEmailBlur = async (field: any, e: any) => {
	// 	field.onBlur(e)
	// 	await handleEmailValidation(e.currentTarget.value)
	// };

	// const handleEmailChange = async (field: any, e: any) => {
	// 	field.onChange(e)
	// 	await handleEmailValidation(e.currentTarget.value)
	// };

	// const handleEmailValidation = async (value: any) => {
	// 	const warningMessage = await validateEmail(value, t)
	// 	setEmailWarning(warningMessage)
	// }
	return (
		<>
			<StyledStepInfoForm.FieldColumns marginBottom={isLoggedIn ? '3rem' : '0rem'}>
				<Label marginTop="6rem" marginBottom="2rem">
					{t('shared.recipient')}
				</Label>
				{isLoggedIn && recipients && recipients.length > 0 && (
					<Column>
						<FormikSelect
							id="recipient"
							name="recipient"
							value={values.recipient}
							options={recipientsAsSelectOptions(recipients)}
							onBlur={setFieldTouched}
							onChange={(e, v) => loadInRecipient(e, v)}
							aria-label="select-recipient"
							placeholder={t('step-type.useRecipient')}
						/>
					</Column>
				)}
				<Column>
					<FormikFieldWrapper
						label={`${t('step-type.where-are-you-delivering-to')}`}
						name="country"
						smallLabel
						languageComponent="registerShipment">
						<FormikSelect
							id="country"
							name="country"
							value={values.country}
							options={countries}
							onBlur={setFieldTouched}
							styles={StyledStepType.countriesSelectStyles}
							onChange={(name: string, option: IFormSelectOption) => {
								if (option.value !== 'IS') {
									setFieldValue('receiverSsn', '');
								}
								setFieldValue(name, option);
							}}
							searchFunction={filterCountries}
							aria-label="select-country"
						/>
					</FormikFieldWrapper>
				</Column>
				{showBusinessAccountWarning && (
					<Column>
						<StyledStepInfoForm.BusinessAccountWarning>
							<Trans
								i18nKey={'step-info.requiresBusinessAccountForCompanySender'}
								ns="registerShipment"
								components={[<A href={businessAccountUrl} target="_blank" />]}
							/>
						</StyledStepInfoForm.BusinessAccountWarning>
					</Column>
				)}
				{false &&
					warnings &&
					warnings.map((c, index) => {
						if (c.countryValue === values.country.value) {
							return (
								<Column key={index}>
									<StyledStepType.StyledErrorMessage>
										{c.warning[lang]}
									</StyledStepType.StyledErrorMessage>
								</Column>
							);
						}
						return null;
					})}
				{false &&
					values.country.value !== 'IS' &&
					warnings &&
					warnings.map((c, index) => {
						if (c.countryValue === 'FOREIGN') {
							return (
								<Column key={index}>
									<StyledStepType.StyledErrorMessage>
										{c.warning[lang]}
									</StyledStepType.StyledErrorMessage>
								</Column>
							);
						}
						return null;
					})}
				{values.country.isClosed && (
					<Column>
						<StyledStepType.StyledErrorMessage>
							{values.country.closedExplanation && values.country.closedExplanation.trim() !== ''
								? values.country.closedExplanation
								: t('step-type.countryClosed')}
						</StyledStepType.StyledErrorMessage>
					</Column>
				)}
				<Column tablet={1 / 2}>
					<FormikFieldWrapper
						name="receiverName"
						label={`${t('step-info.name')}*`}
						smallLabel
						languageComponent="registerShipment">
						<StyledFormikFastField
							type="text"
							id="receiverName"
							name="receiverName"
							placeholder={t('step-info.name')}
							autoComplete="name"
						/>
					</FormikFieldWrapper>
				</Column>
				{values.country.value === 'IS' && (
					<Column tablet={1 / 2}>
						<FormikFieldWrapper
							name="receiverSsn"
							label={t('step-info.ssn')}
							smallLabel
							languageComponent="registerShipment">
							<StyledFormikFastField
								type="text"
								id="receiverSsn"
								name="receiverSsn"
								placeholder={t('step-info.ssn')}
								autoComplete="ssn"
							/>
						</FormikFieldWrapper>
					</Column>
				)}
				<Column tablet={1 / 2}>
					<FormikFieldWrapper
						name="receiverAddress"
						label={`${t('step-info.address')}*`}
						smallLabel
						languageComponent="registerShipment">
						<StyledFormikFastField
							type="text"
							id="receiverAddress"
							name="receiverAddress"
							disabled={readOnlyValuesMapper.receiverAddress}
							placeholder={t('step-info.address')}
							autoComplete="address"
						/>
					</FormikFieldWrapper>
				</Column>
				<Column tablet={1 / 2}>
					{values.country.value !== 'IS' ? (
						<FormikFieldWrapper
							name="receiverForeignPostCode"
							label={`${t('step-info.postcode')}*`}
							smallLabel
							languageComponent="registerShipment">
							<StyledFormikFastField
								id="receiverForeignPostCode"
								name="receiverForeignPostCode"
								type="text"
								placeholder={t('step-info.postcode')}
								autoComplete="postal-code"
							/>
						</FormikFieldWrapper>
					) : (
						<FormikFieldWrapper
							name="receiverPostCode"
							label={`${t('step-info.postcode')}*`}
							smallLabel
							languageComponent="registerShipment">
							<FormikSelect
								id="receiverPostCode"
								name="receiverPostCode"
								value={values.receiverPostCode}
								options={postCodes}
								placeholder={t('step-info.postcode')}
								onBlur={setFieldTouched}
								onChange={setFieldValue}
								autocomplete="postal-code"
							/>
						</FormikFieldWrapper>
					)}
				</Column>
				{values.country.value !== 'IS' && (
					<>
						<Column tablet={1 / 2}>
							<FormikFieldWrapper
								name="receiverForeignAddressExtended"
								label={t('step-info.address-cont')}
								smallLabel
								languageComponent="registerShipment">
								<StyledFormikFastField
									id="receiverForeignAddressExtended"
									name="receiverForeignAddressExtended"
									type="text"
									placeholder={t('step-info.address-cont')}
									autoComplete="address-line2"
								/>
							</FormikFieldWrapper>
						</Column>
						<Column tablet={1 / 2}>
							<FormikFieldWrapper
								name="receiverForeignShipTo"
								label={t('step-info.addressed-to')}
								smallLabel
								languageComponent="registerShipment">
								<StyledFormikFastField
									id="receiverForeignShipTo"
									name="receiverForeignShipTo"
									type="text"
									placeholder={t('step-info.addressed-to')}
								/>
							</FormikFieldWrapper>
						</Column>
						<Column tablet={1 / 2}>
							<FormikFieldWrapper
								name="receiverForeignPlace"
								label={`${t('step-info.place')}*`}
								smallLabel
								languageComponent="registerShipment">
								<StyledFormikFastField
									id="receiverForeignPlace"
									name="receiverForeignPlace"
									type="text"
									placeholder={t('step-info.place')}
									autoComplete="address-level1"
								/>
							</FormikFieldWrapper>
						</Column>
					</>
				)}
				<Column tablet={1 / 2}>
					<FormikFieldWrapper
						name="receiverEmail"
						label={`${t('step-info.email')} ${values.country.value !== 'IS' ? '*' : ''}`}
						smallLabel
						infoText={values.country.value !== 'IS' && 'step-info.recipient-email-info'}
						languageComponent="registerShipment">
						<StyledFormikFastField
							type="receiverEmail"
							id="receiverEmail"
							name="receiverEmail"
							placeholder={t('step-info.email')}
							autoComplete="email"
						/>
						{/* <FastField id="receiverEmail" name="receiverEmail">
							{({ field }) => (
								<div>
									<Input
										type="text"
										id={field.id}
										name={field.name}
										value={field.value}
										onBlur={(e: any) => handleEmailBlur(field, e)}
										onChange={(e: any) => handleEmailChange(field, e)}
										placeholder={t('step-info.email')}
										autoComplete="email"
									/>
								</div>
							)}
						</FastField>
						{emailWarning && (
							<StyledStepInfoForm.WarningMessage>{t(emailWarning)}</StyledStepInfoForm.WarningMessage>
						)} */}
					</FormikFieldWrapper>
				</Column>
				<Column tablet={1 / 2}>
					<FormikFieldWrapper
						name="receiverPhone"
						label={`${t('step-info.phone')} ${goingToPostbox ? '*' : ''}`}
						smallLabel
						infoText="step-info.phone-info"
						languageComponent="registerShipment">
						<PhoneCountryInput
							id="receiverPhone"
							name="receiverPhone"
							value={values.receiverPhone}
							countryCodeName="receiverPhoneCountryCode"
							countryCodeValue={values.receiverPhoneCountryCode}
							countries={countries}
							onBlur={setFieldTouched}
							setFieldValue={setFieldValue}
							defaultCountryCode="IS"
							placeholder={t('step-info.phone')}
							searchPlaceholder={t('step-info.phone-country-search')}
							autoComplete="tel"
						/>
					</FormikFieldWrapper>
				</Column>
			</StyledStepInfoForm.FieldColumns>
			{isLoggedIn && (
				<>
					<FormikFieldWrapper marginTop="2rem" name="saveRecipient" languageComponent="registerShipment">
						<FormikCheckbox
							name="saveRecipient"
							id="saveRecipient"
							label={t('step-info.saveRecipient') as string}
							setFieldValue={setFieldValue}
						/>
					</FormikFieldWrapper>
					{values.recipient && !values.saveRecipient && (
						<FormikFieldWrapper
							marginTop="1rem"
							name="updateRecipient"
							languageComponent="registerShipment">
							<FormikCheckbox
								name="updateRecipient"
								id="updateRecipient"
								label={t('step-info.updateRecipient') as string}
								setFieldValue={setFieldValue}
							/>
						</FormikFieldWrapper>
					)}
				</>
			)}
		</>
	);
};

export default ReceiverFields;
